// services/claimsService.js
import axios from 'axios';

//Avec sous domaines
const API_BASE_URL = 'https://resolvebo.innovationcode.pro/api';
// const API_BASE_URL = 'http://localhost:5000/api';

class ValidationError extends Error {
    constructor(message, validationErrors) {
        super(message);
        this.name = 'ValidationError';
        this.validationErrors = validationErrors;
    }
}

class ClaimsService {
    static instance = null;

    static getInstance() {
        if (!ClaimsService.instance) {
            ClaimsService.instance = new ClaimsService();
        }
        return ClaimsService.instance;
    }

    async createClaim(formData) {
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/Claims`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return data.trackingNumber;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const { data } = error.response;

                // Log détaillé de l'erreur
                console.error('Erreur serveur:', {
                    status: error.response.status,
                    data: data,
                    headers: error.response.headers
                });

                if (data.errors) {
                    const flatErrors = {};
                    Object.entries(data.errors).forEach(([field, messages]) => {
                        flatErrors[field.toLowerCase()] = Array.isArray(messages) ? messages[0] : messages;
                    });
                    throw new ValidationError('Validation errors occurred', flatErrors);
                }

                throw new Error(data.message || 'Une erreur est survenue lors de la soumission');
            }
            throw new Error('Une erreur est survenue lors de la connexion au serveur');
        }
    }

    async addComment(trackingNumber, commentContent, claimData) {
        try {
            if (!claimData || !claimData.details) {
                console.error('Données de réclamation manquantes:', claimData);
                throw new Error('Données de réclamation invalides');
            }

            const authorName = `${claimData.details.civilite} ${claimData.details.nom}`;

            const commentData = {
                trackingNumber: trackingNumber, // Ajout du trackingNumber dans le corps de la requête
                content: commentContent,
                authorName: authorName,
                authorRole: "Client",
                isInternal: false
            };

            console.log('Envoi du commentaire:', commentData);

            const response = await axios.post(
                `${API_BASE_URL}/claims/${trackingNumber}/comments`,
                commentData
            );

            return response.data;
        } catch (error) {
            console.error('Erreur détaillée:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message
            });

            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 404) {
                    throw new Error('Réclamation non trouvée');
                }
                if (error.response.data?.errors) {
                    const errorMessage = Object.values(error.response.data.errors)
                        .flat()
                        .join(', ');
                    throw new Error(`Erreur de validation: ${errorMessage}`);
                }
                throw new Error(error.response.data.message || 'Erreur du serveur');
            }
            throw new Error('Impossible d\'ajouter le commentaire');
        }
    }

    async getClaimByTrackingNumber(trackingNumber) {
        try {
            const response = await axios.get(`${API_BASE_URL}/claims/${trackingNumber}`);
            console.log(response);

            return response.data;
        } catch (error) {
            console.error('Erreur de récupération:', error);
            throw new Error('Impossible de récupérer les détails de la réclamation');
        }
    }

    async markCommentAsRead(trackingNumber, commentId, userRole) {
        console.log(`DÉBUT markCommentAsRead (${userRole}) pour ${commentId}`);

        if (!trackingNumber || !commentId || !userRole) {
            console.error('Paramètres invalides pour marquer un commentaire comme lu', { trackingNumber, commentId, userRole });
            return false;
        }

        try {
            const url = `${this.API_BASE_URL}/claims/${trackingNumber}/comments/${commentId}/read`;
            const payload = { userRole };

            console.log(`Envoi requête à ${url} avec payload:`, payload);

            // Utilisation d'en-têtes simplifiés sans appeler getAuthHeaders
            const headers = {
                'Content-Type': 'application/json'
            };

            // Ajouter un token d'authentification si disponible
            try {
                const token = localStorage.getItem('authToken');
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
            } catch (e) {
                console.warn("Impossible de récupérer le token d'authentification", e);
            }

            const response = await axios.put(
                url,
                payload,
                { headers }
            );

            console.log(`RÉPONSE (${response.status}):`, response.data);

            return response.status === 200 && (response.data.success || response.data);
        } catch (error) {
            console.error(`ERREUR API:`, error);
            console.log(`Message:`, error.message);
            console.log(`Réponse:`, error.response?.data);
            return false;
        }
    }

    async markNewMessagesAsRead() {
        if (!this.trackingNumber || !Array.isArray(this.displayedComments) || this.displayedComments.length === 0) {
            return;
        }

        const userRole = this.currentUser ? 'bank' : 'client';

        try {
            // Filtrer les messages non lus avec des IDs valides
            const unreadMessages = this.displayedComments.filter(comment => {
                // Vérifier la validité de l'ID
                if (!comment || !comment.id || comment.hasTemporaryId) {
                    return false;
                }

                // Ignorer les commentaires internes pour les clients
                if (comment.isInternal && userRole === 'client') {
                    return false;
                }

                // Déterminer si le message est non lu selon le rôle de l'utilisateur
                if (userRole === 'bank') {
                    return (comment.role === 'user' || comment.authorRole === 'Client') && !comment.isReadByBank;
                } else {
                    return (comment.role === 'bank' || comment.authorRole === 'backoffice' || comment.authorRole === 'agent')
                        && !comment.isReadByClient && !comment.isInternal;
                }
            });

            // Si pas de messages non lus à traiter
            if (unreadMessages.length === 0) {
                console.log('Aucun message non lu à marquer');
                return;
            }

            console.log(`Marquage de ${unreadMessages.length} messages comme lus`);

            // Marquer chaque message comme lu
            for (const comment of unreadMessages) {
                try {
                    const result = await this.claimsService.markCommentAsRead(this.trackingNumber, comment.id, userRole);

                    if (result) {
                        console.log(`Message ${comment.id} marqué comme lu avec succès`);

                        // Mettre à jour l'état local
                        this.setDisplayedComments(prev =>
                            prev.map(c =>
                                c.id === comment.id
                                    ? {
                                        ...c,
                                        isReadByClient: userRole === 'client' ? true : c.isReadByClient,
                                        isReadByBank: userRole === 'bank' ? true : c.isReadByBank
                                    }
                                    : c
                            )
                        );
                    } else {
                        console.warn(`Échec du marquage du message ${comment.id} comme lu`);
                    }
                } catch (err) {
                    console.error(`Erreur lors du marquage du message ${comment.id} comme lu:`, err);
                }
            }
        } catch (error) {
            console.error("Erreur générale lors du marquage des messages comme lus:", error);
        }
    }

    // Nouvelles méthodes pour les statistiques et la gestion des réclamations
    async getAllClaims(status = null, limit = null) {
        try {
            let url = `${API_BASE_URL}/claims`;

            // Construire les paramètres de requête
            const params = new URLSearchParams();
            if (status) params.append('status', status);
            if (limit) params.append('limit', limit);

            // Ajouter les paramètres à l'URL s'il y en a
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Erreur de récupération des réclamations:', error);
            throw new Error('Impossible de récupérer la liste des réclamations');
        }
    }

    async getClaimsStats() {
        try {
            const response = await axios.get(`${API_BASE_URL}/claims/stats`);
            return response.data;
        } catch (error) {
            console.error('Erreur de récupération des statistiques:', error);
            throw new Error('Impossible de récupérer les statistiques des réclamations');
        }
    }

    async getClaimsCount(status = null) {
        try {
            let url = `${API_BASE_URL}/claims/count`;

            // Ajouter le paramètre de statut s'il est spécifié
            if (status) {
                url += `?status=${encodeURIComponent(status)}`;
            }

            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Erreur de récupération du nombre de réclamations:', error);
            throw new Error('Impossible de récupérer le nombre de réclamations');
        }
    }
    // Services de gestion des évaluations
    // Ceci est un extrait des méthodes pertinentes, à intégrer dans votre service existant

    /**
     * Vérifie si une réclamation peut être évaluée (clôturée et non déjà évaluée)
     * @param {object} claimData - Données de la réclamation
     * @returns {boolean} - True si la réclamation peut être évaluée
     */
    canRateClaim(claimData) {
        if (!claimData) return false;

        // Une réclamation peut être évaluée si:
        // 1. Elle est clôturée
        // 2. Elle n'est pas déjà évaluée
        const isClosedClaim = claimData.status === 'closed';
        const isAlreadyRated = claimData.rating && claimData.rating.id;

        return isClosedClaim && !isAlreadyRated;
    }

    /**
     * Soumet une évaluation pour une réclamation en utilisant le numéro de suivi
     * @param {string} trackingNumber - Numéro de suivi de la réclamation
     * @param {object} ratingData - Données d'évaluation {rating: number, comment: string}
     * @returns {Promise<object>} - Données de l'évaluation créée
     */
    async rateClaimAsync(trackingNumber, ratingData) {
        try {
            console.log(`Soumission d'évaluation pour la réclamation ${trackingNumber}:`, ratingData);

            // Utiliser le endpoint avec le numéro de suivi plutôt qu'un ID
            const response = await axios.post(
                `${API_BASE_URL}/ratings/claims/${trackingNumber}`,
                {
                    rating: ratingData.rating,
                    comment: ratingData.comment || ''
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Évaluation soumise avec succès:', response.data);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la soumission de l\'évaluation:', error);

            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 404) {
                    throw new Error('Réclamation non trouvée');
                }
                if (error.response.status === 400) {
                    // Extraire les messages d'erreur du serveur
                    const errorData = error.response.data;

                    // Tenter de récupérer le message d'erreur le plus informatif possible
                    let errorMessage = 'Données d\'évaluation invalides';

                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.errors) {
                        if (typeof errorData.errors === 'object') {
                            // Si errors est un objet avec des propriétés, joindre tous les messages
                            errorMessage = Object.values(errorData.errors)
                                .flat()
                                .join(', ');
                        } else if (Array.isArray(errorData.errors)) {
                            // Si errors est un tableau, joindre tous les messages
                            errorMessage = errorData.errors.join(', ');
                        } else if (typeof errorData.errors === 'string') {
                            // Si errors est une simple chaîne
                            errorMessage = errorData.errors;
                        }
                    }

                    throw new Error(errorMessage);
                }

                // Pour les autres erreurs HTTP
                throw new Error(error.response.data.message || 'Erreur du serveur lors de l\'évaluation');
            }

            // Pour les erreurs réseau ou autres
            throw new Error('Impossible de soumettre l\'évaluation. Veuillez réessayer plus tard.');
        }
    }

    /**
     * Récupère l'évaluation d'une réclamation
     * @param {string} claimId - Identifiant de la réclamation
     * @returns {Promise<object|null>} - Données de l'évaluation ou null si non trouvée
     */
    async getClaimRatingAsync(claimId) {
        try {
            console.log(`Récupération de l'évaluation pour la réclamation ${claimId}`);

            const response = await axios.get(`${API_BASE_URL}/ratings/claims/${claimId}`);

            console.log('Évaluation récupérée:', response.data);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération de l\'évaluation:', error);

            if (axios.isAxiosError(error)) {
                if (error.response?.status === 404) {
                    // Réclamation non évaluée - c'est normal, on retourne null
                    console.log('Aucune évaluation trouvée pour cette réclamation');
                    return null;
                }
                throw new Error(error.response?.data?.message || 'Erreur du serveur');
            }
            throw new Error('Impossible de récupérer l\'évaluation. Veuillez réessayer plus tard.');
        }
    }

}

export const claimsService = ClaimsService.getInstance();
export default claimsService;